import Uppy from '@uppy/core'
import '@uppy/core/dist/style.min.css'
import '@uppy/dashboard/dist/style.min.css'
import Webcam from '@uppy/webcam'
import '@uppy/webcam/dist/style.min.css'
import Spanish from '@uppy/locales/lib/es_ES'
import English from '@uppy/locales/lib/en_US'
import French from '@uppy/locales/lib/fr_FR'
import Portuguese from '@uppy/locales/lib/pt_PT'
import PortugueseBR from '@uppy/locales/lib/pt_BR'
import German from '@uppy/locales/lib/de_DE'
import Italian from '@uppy/locales/lib/it_IT'
import { Button } from 'baseui/button'
import { toaster } from 'baseui/toast'
import { useContext, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import UploadStep from '../../components/UploadStep'
import WelcomeStep from '../../components/WelcomeStep'
import { StepperContext, STEPS } from '../../contexts/StepperContext'
import { useUploadDocuments } from '../../hooks/useUploadDocuments'
import operationService, { Stepper } from '../../services/operations-service'
import { Asset } from '../../services/upload-documents'
import { theme } from '../../theming'
import styles from './UploadDocuments.module.scss'
import { useTranslation } from 'react-i18next'

export type FileData = {
  name: string
  mime: string
  ext: string
  content: string
}

const getUppyLocale = (language: string) => {
  const localeMap: Record<string, any> = {
    'es-ES': Spanish,
    'en-GB': English,
    'fr-FR': French,
    'pt-PT': Portuguese,
    'pt-BR': PortugueseBR,
    'de': German,
    'it': Italian
  }
  return localeMap[language] || Spanish
}

const UploadDocuments = () => {
  const navigate = useNavigate()
  const { sendDocument, stepsSequence } = useUploadDocuments()
  const stepperContext = useContext(StepperContext)
  const [searchParams] = useSearchParams()

  const token = searchParams.get('token') || ''
  const oId = searchParams.get('oId') || ''
  const sId = searchParams.get('sId') || ''

  const [stepper, setStepper] = useState<Stepper>()
  const [uppy, setUppy] = useState<any>(null)

  const [currentUploadDocumentStepIndex, setCurrentStepIndex] = useState(0)
  const currentUploadDocumentStep =
    stepsSequence[currentUploadDocumentStepIndex]
  const [uploadedFile, setUploadedFile] = useState<FileData | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const { t, i18n } = useTranslation()

  async function getOperationSteps() {
    try {
      const stepper: Stepper = await operationService.getOperationSteps(
        oId,
        sId,
        token
      )
      setStepper(stepper)
      stepperContext.handleSteps(stepper)
    } catch (error) {
      // El interceptor global de api.ts ya muestra el error en un toaster
      navigate(`/`)
      return
    }
  }

  const navigateToNextOnboardingStep = () => {
    if (token) {
      // En estado 7 el firmante ya ha firmado - continuar con proceso post-firma
      if (stepper?.signer.state === 7) {
        if (stepper.signer?.requireSignatureOtp) {
          navigate(`/otp?${searchParams.toString()}`)
        } else if (stepper.signer?.requireEmailValidation) {
          navigate(
            `/finishedsign?requireEmailValidation=true&oId=${oId}&sId=${sId}`
          )
        } else {
          navigate(`/finishedsign?oId=${oId}&sId=${sId}`)
        }
      } else {
        if (stepperContext.steps.includes(STEPS.DOCUMENT_MODIFICATION)) {
          navigate(`/edit-documents?${searchParams.toString()}&token=${token}`)
        } else {
          navigate(`/sign?${searchParams.toString()}`)
        }
      }
    } else {
      navigate(`/`)
    }
  }

  const goToNextUploadDocumentStep = async () => {
    if (currentUploadDocumentStep.model !== 'WELCOME' && uploadedFile) {
      setIsLoading(true)

      const assetBody: Partial<Asset> = {
        model: currentUploadDocumentStep.model as any,
        name: uploadedFile.name,
        mime: uploadedFile.mime,
        ext: uploadedFile.ext,
        content: uploadedFile.content
      }
      try {
        await sendDocument(assetBody)
        clearUppyFiles()
        setUploadedFile(null)
      } catch (error) {
        toaster.negative('Error al subir el documento', {
          autoHideDuration: 3000,
          overrides: {
            Body: {
              style: {
                width: '100%',
                maxWidth: '20rem',
                backgroundColor: `${theme.style.primitives.primary}`,
                position: 'absolute',
                marginTop: '1rem'
              }
            }
          }
        })

        setIsLoading(false)

        return
      } finally {
        setIsLoading(false)
      }
    }

    const nextIndex = currentUploadDocumentStepIndex + 1
    if (nextIndex < stepsSequence.length) {
      setCurrentStepIndex(nextIndex)
    } else {
      navigateToNextOnboardingStep()
    }
  }

  const clearUppyFiles = () => {
    if (uppy) {
      uppy.getFiles().forEach((file: any) => {
        uppy.removeFile(file.id)
      })
    }
  }

  useEffect(() => {
    stepperContext.setCurrentStep(STEPS.UPLOAD_DOCUMENTS)
    getOperationSteps()
  }, [])

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [currentUploadDocumentStepIndex])

  useEffect(() => {
    const uppyInstance = new Uppy({
      restrictions: {
        maxFileSize: 10000000, // 10MB
        allowedFileTypes: ['image/png', 'image/jpeg', 'application/pdf'],
        maxNumberOfFiles: 1
      },
      autoProceed: false,
      locale: getUppyLocale(i18n.language)
    }).use(Webcam, {
      modes: ['picture'],
      mirror: true,
      locale: getUppyLocale(i18n.language)
    })

    uppyInstance.on('file-added', (file) => {
      const reader = new FileReader()
      reader.readAsDataURL(file.data)
      reader.onloadend = function () {
        const base64data = reader.result as string
        // Extraer solo la parte base64 del data URL
        const [, base64] = base64data.replace(/ /g, '').split(';base64,')

        const fileData: FileData = {
          name: file.name,
          mime: file.type || 'application/octet-stream',
          ext: file.extension,
          content: base64
        }

        setUploadedFile(fileData)
      }
    })

    uppyInstance.on('file-removed', () => {
      setUploadedFile(null)
    })

    setUppy(uppyInstance)

    return () => uppyInstance.close()
  }, [i18n.language])

  return (
    <div className={styles.container}>
      {currentUploadDocumentStep.model === 'WELCOME' ? (
        <WelcomeStep />
      ) : (
        <UploadStep
          title={currentUploadDocumentStep.title}
          description={currentUploadDocumentStep.description}
          uppy={uppy}
        />
      )}

      <div
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingBottom: '24px',
          gap: '16px'
        }}
      >
        <Button
          onClick={goToNextUploadDocumentStep}
          disabled={
            (currentUploadDocumentStep.model !== 'WELCOME' && !uploadedFile) ||
            isLoading
          }
          style={{
            width: '280px',
            border: 'none',
            borderRadius: '8px',
            padding: '12px 32px',
            fontSize: '18px',
            cursor:
              (currentUploadDocumentStep.model !== 'WELCOME' &&
                !uploadedFile) ||
              isLoading
                ? 'default'
                : 'pointer'
          }}
        >
          {currentUploadDocumentStep.model === 'WELCOME'
            ? t('START')
            : isLoading
            ? t('LOADING')
            : currentUploadDocumentStepIndex === stepsSequence.length - 1
            ? stepper?.signer.state === 7
              ? t('FINISH')
              : t('GO_TO_SIGN')
            : t('CONTINUE')}
        </Button>

        {currentUploadDocumentStep.model === 'WELCOME' &&
          stepper?.signer.state !== 7 && (
            <Button
              kind="secondary"
              size="compact"
              onClick={navigateToNextOnboardingStep}
              style={{
                fontSize: '14px',
                padding: '8px 16px',
                backgroundColor: 'transparent'
              }}
            >
              {t('PROVIDE_DOCUMENTATION_LATER')}
            </Button>
          )}
      </div>
    </div>
  )
}

export default UploadDocuments
